import React, { useEffect, useState, useContext } from "react";
import { getClasses } from "../../api/classSubjectApi";
import { AuthContext } from "../../store/AuthContext";
import {
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import StudentLayout from "../../layouts/StudentLayout";

/**
 * StudentSelectClass.js
 * Fully merged & TV-optimized version:
 * - Hides header when loaded inside Android app/webview
 * - Auto-focuses first class card on TV
 * - DPAD navigation (Up/Down/Left/Right + Enter)
 * - TV-specific visual enhancements and focus styles
 */

export default function StudentSelectClass() {
  const { token } = useContext(AuthContext);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();

  // Media queries for responsive design
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isTV = useMediaQuery("(min-width: 1920px)");

  // Detect whether running inside Android App / WebView / TV
  const isAndroidApp = () =>
    typeof navigator !== "undefined" &&
    (navigator.userAgent.includes("VrPlayApp") ||
      navigator.userAgent.includes("AndroidWebView") ||
      navigator.userAgent.includes("Android") && navigator.userAgent.includes("WebView") ||
      typeof window !== "undefined" && (window.AndroidWebView !== undefined || window.ReactNativeWebView !== undefined));

  const isAndroidTV = () =>
    typeof navigator !== "undefined" &&
    (navigator.userAgent.includes("AndroidTV") ||
      navigator.userAgent.includes("TV") ||
      isAndroidApp());

  // Load classes on mount
  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClasses = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getClasses(token);
      // Expecting res.data to be an array of classes
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("Failed to load classes. Please try again.");
      console.error("Error loading classes:", err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-focus first card on TV/Android app after classes load
  useEffect(() => {
    if (!loading && classes.length > 0 && isAndroidTV()) {
      // small delay to ensure DOM is rendered
      setTimeout(() => {
        const first = document.querySelector(".tv-card, .class-card");
        if (first && typeof first.focus === "function") {
          first.focus();
        }
      }, 120);
    }
  }, [loading, classes]); // eslint-disable-line

  // DPAD navigation handler (TV)
  useEffect(() => {
    if (!isAndroidTV()) return;

    const handleKeyDown = (e) => {
      // Select all focusable tv-card elements
      const focusable = Array.from(document.querySelectorAll(".tv-card"));
      if (!focusable || focusable.length === 0) return;

      const currentEl = document.activeElement;
      let index = focusable.indexOf(currentEl);

      // If nothing focused, focus first
      if (index === -1) {
        focusable[0].focus();
        return;
      }

      // Determine number of columns in grid for vertical movement
      // Heuristic: columns depend on viewport size; we align with getGridConfig mapping
      let columns = 1;
      if (isTV) columns = 4;
      else if (isDesktop) columns = 3;
      else if (isTablet) columns = 2;
      else columns = 1;

      switch (e.key) {
        case "ArrowRight":
        case "Right":
          if (index + 1 < focusable.length) focusable[index + 1].focus();
          e.preventDefault();
          break;
        case "ArrowLeft":
        case "Left":
          if (index - 1 >= 0) focusable[index - 1].focus();
          e.preventDefault();
          break;
        case "ArrowDown":
        case "Down":
          if (index + columns < focusable.length) focusable[index + columns].focus();
          e.preventDefault();
          break;
        case "ArrowUp":
        case "Up":
          if (index - columns >= 0) focusable[index - columns].focus();
          e.preventDefault();
          break;
        case "Enter":
        case "NumpadEnter":
        case " ":
          // Trigger click on focused element
          if (document.activeElement) {
            document.activeElement.click();
          }
          e.preventDefault();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, isTV, isDesktop, isTablet]); // re-register when layout changes

  // Enhanced card styling based on device type
  const getCardStyles = () => {
    if (isTV) {
      return {
        cursor: "pointer",
        height: 300,
        padding: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.mode === "dark" ? "#111" : "#fff",
        color: theme.palette.text.primary,
        border: "2px solid transparent",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "scale(1.03)",
          boxShadow: theme.shadows[10],
        },
        "&:focus": {
          transform: "scale(1.08)",
          boxShadow: `0 0 30px ${theme.palette.primary.main}`,
          border: `3px solid ${theme.palette.primary.main}`,
          outline: "none",
          zIndex: 10,
        },
      };
    }

    if (isDesktop) {
      return {
        cursor: "pointer",
        height: 200,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[6],
        },
        "&:focus-within": {
          transform: "translateY(-2px)",
          boxShadow: `0 0 0 3px ${theme.palette.primary.main}, ${theme.shadows[4]}`,
        },
      };
    }

    if (isTablet) {
      return {
        cursor: "pointer",
        height: 160,
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
        },
        "&:focus-within": {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
        },
      };
    }

    // Mobile styles
    return {
      cursor: "pointer",
      height: 120,
      transition: "all 0.2s ease",
      "&:active": {
        backgroundColor: theme.palette.action.selected,
        transform: "scale(0.98)",
      },
      "&:focus-within": {
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
      },
    };
  };

  // Responsive grid configuration
  const getGridConfig = () => {
    if (isTV) return { xs: 12, sm: 6, md: 4, lg: 3 };
    if (isDesktop) return { xs: 12, sm: 6, md: 4 };
    if (isTablet) return { xs: 12, sm: 6 };
    return { xs: 12 }; // Mobile
  };

  // Responsive typography variant
  const getTitleVariant = () => {
    if (isTV) return "h2";
    if (isDesktop) return "h3";
    if (isTablet) return "h4";
    return "h5";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        flexDirection="column"
        gap={3}
      >
        <CircularProgress size={isTV ? 80 : isDesktop ? 60 : 40} thickness={isTV ? 4 : 3.6} />
        <Typography variant={isMobile ? "h6" : "h5"} color="text.secondary">
          Loading Classes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth={isTV ? "xl" : "lg"} sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadClasses}>
              RETRY
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <StudentLayout>
      <Container
        maxWidth={isTV ? "xl" : "lg"}
        sx={{
          py: isTV ? 6 : isDesktop ? 5 : 4,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header Section (hide when inside Android app / TV WebView) */}
        {!isAndroidApp() && (
          <Box
            sx={{
              textAlign: isMobile ? "center" : "left",
              mb: isTV ? 6 : isDesktop ? 5 : 4,
            }}
          >
            <Typography
              variant={getTitleVariant()}
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 2,
              }}
            >
              Select Your Class
            </Typography>

            <Typography variant={isTV ? "h6" : isMobile ? "body2" : "body1"} color="text.secondary" sx={{ maxWidth: isTV ? "80%" : "100%" }}>
              Choose your class to explore available subjects and start learning
            </Typography>
          </Box>
        )}

        {/* Classes Grid */}
        <Grid container spacing={isTV ? 4 : isDesktop ? 3 : 2}>
          {classes.map((cls) => (
            <Grid item {...getGridConfig()} key={cls.id}>
              <Card
                className={`tv-card class-card`}
                tabIndex={0}
                sx={{
                  ...getCardStyles(),
                  // Common tv focus styles (ensures consistent visual)
                  "&:focus": {
                    transform: isTV ? "scale(1.08)" : undefined,
                    boxShadow: isTV ? `0 0 30px ${theme.palette.primary.main}` : undefined,
                  },
                }}
                onClick={() => navigate(`/student/select-subject?class_id=${cls.id}`)}
                component="article"
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/student/select-subject?class_id=${cls.id}`);
                  }
                }}
              >
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    p: isTV ? 4 : isDesktop ? 3 : 2,
                    "&:last-child": { pb: isTV ? 4 : isDesktop ? 3 : 2 },
                  }}
                >
                  <Typography
                    variant={isTV ? "h2" : isDesktop ? "h4" : isTablet ? "h5" : "h6"}
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      color: isTV ? "#fff" : "primary.main",
                      textShadow: isTV ? "0 4px 20px rgba(0,0,0,0.6)" : "none",
                    }}
                  >
                    Class {cls.class}
                  </Typography>

                  {/* Optional: Add class description or additional info */}
                  {cls.description && (
                    <Typography variant={isTV ? "body1" : "body2"} color="text.secondary" sx={{ mt: 1 }}>
                      {cls.description}
                    </Typography>
                  )}

                  {/* Visual indicator for focus state */}
                  <Box
                    sx={{
                      mt: 2,
                      width: isTV ? 120 : isDesktop ? 80 : 60,
                      height: 6,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      borderRadius: 2,
                      opacity: 0.95,
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {classes.length === 0 && !loading && (
          <Box textAlign="center" py={8} sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography variant={isMobile ? "h6" : "h5"} color="text.secondary" gutterBottom>
              No classes available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check back later or contact your administrator.
            </Typography>
          </Box>
        )}
      </Container>
    </StudentLayout>
  );
}
